/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2024 Wecare Dynamics Inc.



 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, Suspense } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React TS exampless
import Sidenav from "examples/Sidenav";

// Material Dashboard 2 PRO React TS themes
import { getThemeForRole } from "assets/theme";

// Material Dashboard 2 PRO React TS routes
import { iRoute, useAuthRoutes } from "routes";

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController } from "context";
import { AuthProvider, useAuth } from "context/userContext";
import RouteAccessGuard from "components/RouteAccessGuard";

// Images
import brandLogo from "assets/images/logos/Wecare-logo.svg";
import { isNotNil } from "helpers/utils";

interface RouteAccessRequirements {
    requiredMenuKey?: string;
    requiredRoles?: string[];
}

const RouteLoadingFallback = () => (
    <Box
        role="status"
        aria-live="polite"
        minHeight="40vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1.5}
        color="text.secondary">
        <CircularProgress size={24} color="inherit" />
        <Box component="span" fontSize={14} fontWeight={600}>
            Loading page...
        </Box>
    </Box>
);

const RoutesComponent = ({
    handleOnMouseEnter,
    handleOnMouseLeave,
    layout,
}: {
    handleOnMouseEnter: () => void;
    handleOnMouseLeave: () => void;
    layout: string;
}) => {
    const routes = useAuthRoutes();
    const { authStatus, currentUser } = useAuth();
    const theme = getThemeForRole(currentUser?.role);

    const getRouteElement = (route: iRoute, inheritedAccess: RouteAccessRequirements) => {
        if (!route.component) return null;

        if (route.isPublic) {
            return route.component;
        }

        const requiredMenuKey = route.requiredMenuKey ?? inheritedAccess.requiredMenuKey;
        const requiredRoles = route.requiredRoles ?? inheritedAccess.requiredRoles;

        return (
            <RouteAccessGuard requiredMenuKey={requiredMenuKey} requiredRoles={requiredRoles}>
                {route.component}
            </RouteAccessGuard>
        );
    };

    const getRoutes = (allRoutes: iRoute[], inheritedAccess: RouteAccessRequirements = {}): any =>
        allRoutes.map((route) => {
            const nextInheritedAccess: RouteAccessRequirements = {
                requiredMenuKey: route.requiredMenuKey ?? inheritedAccess.requiredMenuKey,
                requiredRoles: route.requiredRoles ?? inheritedAccess.requiredRoles,
            };

            // Recursive routes
            if (isNotNil(route.collapse)) {
                return getRoutes(route.collapse, nextInheritedAccess);
            }

            if (route.route) {
                return <Route path={route.route} element={getRouteElement(route, inheritedAccess)} key={route.key} />;
            }

            return null;
        });

    const spsRoute = () => {
        const defaultRoute =
            currentUser?.role === "installer"
                ? "/device-installation"
                : currentUser?.role === "user"
                  ? "/overview/profile"
                  : "/dashboard";

        return (
            <Route
                path="*"
                element={
                    <RouteAccessGuard>
                        <Navigate to={defaultRoute} replace />
                    </RouteAccessGuard>
                }
            />
        );
    };
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {layout === "dashboard" && authStatus === "authenticated" && currentUser?.role !== "user" && (
                <NavBar handleOnMouseEnter={handleOnMouseEnter} handleOnMouseLeave={handleOnMouseLeave} />
            )}
            <Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                    {getRoutes(routes)}
                    {spsRoute()}
                </Routes>
            </Suspense>
        </ThemeProvider>
    );
};

const NavBar = ({
    handleOnMouseEnter,
    handleOnMouseLeave,
}: {
    handleOnMouseEnter: () => void;
    handleOnMouseLeave: () => void;
}) => {
    const routes = useAuthRoutes();
    return (
        <Sidenav
            brand={brandLogo}
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
        />
    );
};

export default function App() {
    const [controller] = useMaterialUIController();
    const { miniSidenav, layout } = controller;
    const [onMouseEnter, setOnMouseEnter] = useState(false);
    const { pathname } = useLocation();

    if (process.env.NODE_ENV === "production") {
        console.log = () => {};
    }

    // Open sidenav when mouse enter on mini sidenav
    const handleOnMouseEnter = () => {
        if (miniSidenav && !onMouseEnter) {
            setOnMouseEnter(true);
        }
    };

    // Close sidenav when mouse leave mini sidenav
    const handleOnMouseLeave = () => {
        if (onMouseEnter) {
            setOnMouseEnter(false);
        }
    };

    // Setting page scroll to 0 when changing the route
    useEffect(() => {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
    }, [pathname]);

    return (
        <Suspense fallback={<RouteLoadingFallback />}>
            <AuthProvider>
                <RoutesComponent
                    layout={layout}
                    handleOnMouseEnter={handleOnMouseEnter}
                    handleOnMouseLeave={handleOnMouseLeave}
                />
            </AuthProvider>
        </Suspense>
    );
}
